import React, { useContext } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const BackgroundAnimation = () => (
    <div className="absolute inset-0 overflow-hidden -z-10">
        <motion.div
            className="w-64 h-64 bg-alphaGreen-200 dark:bg-alphaOrange-500 rounded-full opacity-30 blur-2xl"
            animate={{ x: [0, 200, 0], y: [0, -100, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
            className="w-96 h-96 bg-alphaOrange-300 dark:bg-alphaGreen-600 rounded-full opacity-20 blur-3xl absolute top-10 left-20"
            animate={{ x: [0, -200, 0], y: [0, 150, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
            className="w-72 h-72 bg-alphaGreen-300 dark:bg-alphaOrange-700 rounded-full bg-opacity-20 blur-2xl absolute bottom-10 right-10"
            animate={{ x: [0, 150, 0], y: [0, -150, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
    </div>
);

const Instructions = () => {
    const { token } = useContext(AuthContext);
    const stepsToRegister = [
        "اضغط على 'حساب جديد' بالأعلي إذا لم يكن لديك حساب على المنصة بعد.",
        "قم بملء البيانات المطلوبة بعناية، ثم اضغط على 'إنشاء الحساب'.",
        "بعد نجاح إنشاء الحساب، ستنتقل تلقائيًا إلى الصفحة الرئيسية.",
    ];
    const stepsToSubscribe = [
        "انتقل إلى الأسفل واختر الدورة التدريبية التي ترغب في الاشتراك بها.",
        "اضغط على 'اشترك الآن' بجانب الدورة التدريبية، ثم ستنتقل إلى صفحة الفاتورة.",
        <>
            <div className="p-6 bg-alphaGreen-50 smooth dark:bg-alphaOrange-900/10 border border-dashed border-alphaGreen-400 dark:border-alphaOrange-500 rounded-lg shadow-md flex flex-col md:flex-row items-center md:items-start justify-center gap-6">
                {/* Wallet Method */}
                <div className="flex-1 h-auto min-h-[220px] bg-white smooth dark:bg-gray-900 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold smooth text-alphaGreen-700 dark:text-alphaOrange-400">
                        الطريقة الأولى: المحفظة
                    </h3>
                    <ul className="list-disc text-sm list-inside smooth text-gray-600 dark:text-gray-300 space-y-2 mt-3">
                        <li className="list-disc mr-5">
                            إذا كان لديك رصيد بالمحفظة، يمكنك الاشتراك بسهولة.
                        </li>
                        <li className="list-disc mr-5">
                            سيتم خصم سعر الدورة من رصيدك في أقل من ثانية.
                        </li>
                        <li className="list-disc mr-5">
                            إذا كان رصيد محفظتك غير كافٍ، يمكنك الذهاب إلى أقرب منفذ للدفع وتحويل
                            الرصيد المرغوب إضافته.
                        </li>
                        <li className="list-disc mr-5">
                            <span>
                                اذهب إلى{" "}
                                <Link
                                    to={token ? "/me/users/wallet" : "/login"}
                                    className="text-teal-600 smooth dark:text-teal-400 underline hover:text-teal-700"
                                >
                                    صفحة شحن المحفظة
                                </Link>{" "}
                                وأرسل الفاتورة الخاصة بك. سيتم مراجعتها من قبل المسؤول وتأكيد عملية
                                الدفع.
                            </span>
                        </li>
                    </ul>
                </div>

                {/* Invoice Method */}
                <div className="flex-1 min-h-[220px] bg-white smooth dark:bg-gray-900 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold smooth text-alphaGreen-700 dark:text-alphaOrange-400">
                        الطريقة الثانية: الفاتورة
                    </h3>
                    <ul className="list-disc list-inside text-sm smooth text-gray-600 dark:text-gray-300 space-y-2 mt-3">
                        <li className="list-disc mr-5">قم بإنشاء فاتورة لخدمة الدفع المختارة.</li>
                        <li className="list-disc mr-5">اختر طريقة الدفع التي تناسبك.</li>
                        <li className="list-disc mr-5">
                            ادفع باستخدام الفاتورة، وسيتم تفعيل الدورة تلقائيًا خلال دقائق.
                        </li>
                    </ul>
                </div>
            </div>
        </>,
        "بعد إتمام الدفع، سواء من خلال المحفظة أو الفاتورة، ستتم إضافة الدورة إلى حسابك تلقائيًا.",
        "ستظهر علامة 'أنت مشترك بهذه الدورة' بجانب الدورة التدريبية.",
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="relative negative-nav-margin py-[10rem] min-h-screen px-6 sm:px-12 font-ibm-arabic lg:px-24"
        >
            <BackgroundAnimation />

            <div className="max-w-5xl mx-auto text-center">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    className="text-4xl font-extrabold text-alphaGreen-700 dark:text-alphaAccentText smooth mb-12"
                >
                    تعليمات التسجيل والاشتراك في دورة تدريبية
                </motion.h1>

                <div className="space-y-12">
                    {/* Steps to Register */}
                    <motion.section
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="shadow-lg border rounded-xl p-6 bg-alphaGreen-50 dark:bg-alphaOrange-900/20 border-dashed border-alphaGreen-500 dark:border-alphaOrange-500 smooth"
                    >
                        <h2 className="text-3xl font-semibold smooth text-alphaGreen-700 dark:text-alphaAccentText mb-6">
                            خطوات التسجيل
                        </h2>
                        <ul className="list-disc text-right smooth space-y-4 pl-6 text-gray-600 dark:text-gray-300">
                            {stepsToRegister.map((step, index) => (
                                <li key={index} className="list-disc mr-5">
                                    {step}
                                </li>
                            ))}
                        </ul>
                    </motion.section>

                    {/* Steps to Subscribe */}
                    <motion.section
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="shadow-lg border rounded-xl p-6 bg-alphaGreen-50 dark:bg-alphaOrange-900/20 border-dashed border-alphaGreen-500 dark:border-alphaOrange-500 smooth"
                    >
                        <h2 className="text-3xl font-semibold smooth text-alphaGreen-700 dark:text-alphaAccentText mb-6">
                            خطوات الاشتراك في الدورة
                        </h2>
                        <ul className="list-disc text-right smooth space-y-4 pl-6 text-gray-600 dark:text-gray-300">
                            {stepsToSubscribe.map((step, index) => (
                                <li key={index} className="list-disc mr-5">
                                    {step}
                                </li>
                            ))}
                        </ul>
                    </motion.section>
                </div>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    className="text-lg text-gray-700 smooth dark:text-gray-300 mt-12"
                >
                    إذا واجهتك أي مشكلة، يُرجى التواصل معنا عبر الواتساب وسنساعدك في أسرع وقت ❤️
                </motion.p>
            </div>
        </motion.div>
    );
};

export default Instructions;
